import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [showAll, setShowAll] = useState(false); // Option to show all records
  const { formatPrice } = useCurrency();
  const { isAuthenticated, user, getToken } = useAuth();
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL || '';

  // Get status from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const initialStatus = urlParams.get('status') || '';

  const [filters, setFilters] = useState({
    status: initialStatus,
    start_date: '',
    end_date: '',
    search: '',
    payment_status: '',
    payment_method: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken(true); // Dashboard token
      if (!token) {
        navigate('/dashboard/login');
        return;
      }

      // If showAll is true, set show_all parameter, otherwise use pagination
      const queryParams = new URLSearchParams({
        ...(showAll ? { show_all: 'true' } : {
          page: pagination.page.toString(),
          limit: pagination.limit.toString()
        }),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      });

      const url = `${API_BASE}/api/orders/admin/all?${queryParams}`;
      console.log('🔍 [AdminOrders] Fetching orders from:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 [AdminOrders] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [AdminOrders] API Error:', response.status, errorText);
        toast.error(`Failed to fetch orders: ${response.status}`);
        return;
      }

      const data = await response.json();
      console.log('🔍 [AdminOrders] Full response data:', JSON.stringify(data, null, 2));
      console.log('🔍 [AdminOrders] Response success:', data.success);
      console.log('🔍 [AdminOrders] Data object:', data.data);
      console.log('🔍 [AdminOrders] Orders array:', data.data?.orders);
      console.log('🔍 [AdminOrders] Orders count:', data.data?.orders?.length || 0);
      console.log('🔍 [AdminOrders] Orders type:', Array.isArray(data.data?.orders) ? 'Array' : typeof data.data?.orders);
      console.log('🔍 [AdminOrders] Pagination:', data.data?.pagination);
      
      if (data.success) {
        const ordersArray = data.data?.orders;
        console.log('✅ [AdminOrders] Orders received:', ordersArray?.length || 0);
        
        if (ordersArray && Array.isArray(ordersArray) && ordersArray.length > 0) {
          console.log('📊 [AdminOrders] First order sample:', JSON.stringify(ordersArray[0], null, 2));
          setOrders(ordersArray);
        } else {
          console.warn('⚠️ [AdminOrders] No orders in response, but success=true');
          console.warn('⚠️ [AdminOrders] Orders value:', ordersArray);
          console.warn('⚠️ [AdminOrders] Is array?', Array.isArray(ordersArray));
          setOrders([]);
        }
        if (!showAll) {
          setPagination(prev => ({
            ...prev,
            total: data.data.pagination?.total || 0,
            pages: data.data.pagination?.pages || 0
          }));
        } else {
          // When showing all, set pagination to reflect all records
          setPagination(prev => ({
            ...prev,
            total: data.data.orders?.length || 0,
            pages: 1
          }));
        }
      } else {
        console.error('❌ [AdminOrders] API returned success=false:', data);
        toast.error(data.message || 'Failed to fetch orders');
        setOrders([]);
      }
    } catch (error) {
      console.error('❌ [AdminOrders] Error fetching orders:', error);
      toast.error('Failed to load orders. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, filters, getToken, navigate, pagination.limit, pagination.page, showAll]);

  useEffect(() => {
    if (isAuthenticated && (user?.role === 'admin' || user?.role === 'employee')) {
      fetchOrders();
    } else if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [fetchOrders, isAuthenticated, navigate, user?.role]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      start_date: '',
      end_date: '',
      search: '',
      payment_status: '',
      payment_method: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setShowAll(false);
  };

  const getStatusColor = (status) => {
    const statusUpper = (status || '').toUpperCase();
    switch (statusUpper) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'PACKING':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'SHIPPED':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'OUT_FOR_DELIVERY':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'DELIVERED':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'PENDING';
    return status.replace(/_/g, ' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Order Management</h1>
        <p className="text-gray-600">View and manage all customer orders</p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Order ID, Phone, Email, or Customer Name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Show All Toggle */}
          <button
            onClick={() => {
              setShowAll(!showAll);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showAll 
                ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700' 
                : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            <span className="text-sm font-medium">{showAll ? 'Show Paginated' : 'Show All'}</span>
          </button>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FunnelIcon className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Order Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="PACKING">Packing</option>
                <option value="SHIPPED">Shipped</option>
                <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                <option value="DELIVERED">Delivered</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>

            {/* Payment Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
              <select
                value={filters.payment_status}
                onChange={(e) => handleFilterChange('payment_status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Payment Status</option>
                <option value="PAID">Paid</option>
                <option value="UNPAID">Unpaid</option>
                <option value="REFUNDED">Refunded</option>
              </select>
            </div>

            {/* Payment Method Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
              <select
                value={filters.payment_method}
                onChange={(e) => handleFilterChange('payment_method', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All Methods</option>
                <option value="credit_card">Credit Card</option>
                <option value="cod">Cash on Delivery</option>
                <option value="gpay">Google Pay</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="xl:hidden divide-y divide-gray-200">
          {orders.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500">
              No orders found
            </div>
          ) : (
            orders.map((order) => (
              <div key={order.id} className="p-4 space-y-4">
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-base font-semibold text-gray-900">
                      {order.order_number || `#${order.id}`}
                    </div>
                    <div className="text-xs text-gray-500">ID: {order.id}</div>
                  </div>
                  <Link
                    to={`/dashboard/orders/${order.id}`}
                    className="inline-flex w-full items-center justify-center gap-1 rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 sm:w-auto"
                  >
                    <EyeIcon className="w-4 h-4" />
                    Details
                  </Link>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Customer</div>
                    <div className="mt-1 text-sm text-gray-900">{order.customer_name || 'N/A'}</div>
                    {order.email && <div className="text-sm text-gray-500 break-all">{order.email}</div>}
                    {order.phone && <div className="text-xs text-gray-400">{order.phone}</div>}
                  </div>

                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Date</div>
                    <div className="mt-1 text-sm text-gray-900">{formatDate(order.created_at)}</div>
                    {order.updated_at && order.updated_at !== order.created_at && (
                      <div className="text-xs text-gray-400">Updated: {formatDate(order.updated_at)}</div>
                    )}
                  </div>

                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Amount</div>
                    <div className="mt-1 text-sm font-semibold text-gray-900">
                      {formatPrice(order.total_amount || 0)}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Items</div>
                    <div className="mt-1 text-sm text-gray-900">{order.item_count || 0} items</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                      order.status
                    )}`}
                  >
                    {formatStatus(order.status)}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                      order.payment_status === 'PAID'
                        ? 'bg-green-100 text-green-800 border-green-300'
                        : order.payment_status === 'REFUNDED'
                        ? 'bg-red-100 text-red-800 border-red-300'
                        : 'bg-yellow-100 text-yellow-800 border-yellow-300'
                    }`}
                  >
                    {order.payment_status || 'UNPAID'}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold border border-gray-300 bg-gray-50 text-gray-700 capitalize">
                    {(order.payment_method || '').replace('_', ' ')}
                  </span>
                </div>

                <Link
                  to={`/dashboard/orders/${order.id}`}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-3 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  <EyeIcon className="w-4 h-4" />
                  Open Full Order Details
                </Link>
              </div>
            ))
          )}
        </div>

        <div className="hidden lg:block overflow-x-auto overscroll-x-contain">
          <table className="min-w-[960px] w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">
                        {order.order_number || `#${order.id}`}
                      </div>
                      <div className="text-xs text-gray-500">ID: {order.id}</div>
                      <Link
                        to={`/dashboard/orders/${order.id}`}
                        className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-900"
                      >
                        <EyeIcon className="w-4 h-4" />
                        Open details
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{order.customer_name || 'N/A'}</div>
                      <div className="text-sm break-all text-gray-500">{order.email || ''}</div>
                      <div className="text-xs text-gray-400">{order.phone || ''}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{formatDate(order.created_at)}</div>
                      {order.updated_at && order.updated_at !== order.created_at && (
                        <div className="text-xs text-gray-400">Updated: {formatDate(order.updated_at)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatPrice(order.total_amount || 0)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                          order.payment_status === 'PAID'
                            ? 'bg-green-100 text-green-800 border-green-300'
                            : order.payment_status === 'REFUNDED'
                            ? 'bg-red-100 text-red-800 border-red-300'
                            : 'bg-yellow-100 text-yellow-800 border-yellow-300'
                        }`}
                      >
                        {order.payment_status || 'UNPAID'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 capitalize">
                        {(order.payment_method || '').replace('_', ' ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                          order.status
                        )}`}
                      >
                        {formatStatus(order.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{order.item_count || 0} items</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/dashboard/orders/${order.id}`}
                        className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-900"
                      >
                        <EyeIcon className="w-4 h-4" />
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!showAll && pagination.pages > 1 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} orders
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                Next
              </button>
            </div>
          </div>
        )}
        {showAll && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-700 font-semibold">
              Showing all {orders.length} orders
            </div>
            <div className="text-xs text-gray-500">
              All data from database displayed
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;

